import * as React from 'react';
import { memo, useCallback } from 'react';

import { PopupNames } from 'core';
import { useTasks, usePopupData, usePopupStateOperations } from 'core/hooks';
import {
  ITitleAddonContext,
  TaskCreateModal,
} from 'pages/_detailShared/TaskBoard/TaskCreateModal';
import { ICreateTaskDraft } from 'pages/_detailShared/TaskBoard/taskBuckets';
import { TaskSmartLinkChips } from 'pages/_detailShared/TaskBoard/TaskSmartLinkChips';

/** Умные чипы связей под названием — те же, что у доски (ТЗ 0109). */
const titleAddon = (ctx: ITitleAddonContext) => (
  <TaskSmartLinkChips
    title={ctx.title}
    links={ctx.links}
    onLinks={ctx.onLinks}
    registerSettle={ctx.registerSettle}
  />
);

/** popupData shape for the palette-dispatched «create task» popup. */
export interface ITaskCreatePopupData {
  studentId?: string;
  groupId?: string;
}

/**
 * Registry wrapper for the shared `TaskCreateModal`, dispatched by the palette
 * via `showPopup(PopupNames.taskCreatePopup, { studentId | groupId })`. Owns
 * `useTasks(scope)` and maps the modal draft onto its `add({ title, description,
 * dueDate, assigneeId, priority })` exactly like `TasksPanel` — no new add path.
 */
export const TaskCreatePopup = memo(() => {
  const data = usePopupData(PopupNames.taskCreatePopup);
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();
  const { add, saving } = useTasks({
    studentId: data?.studentId,
    groupId: data?.groupId,
  });

  const close = useCallback(() => hidePopup(PopupNames.taskCreatePopup), [
    hidePopup,
  ]);

  const onCreate = useCallback(
    (
      draft: ICreateTaskDraft,
      _openAfter?: boolean,
      files?: File[],
    ): Promise<boolean> =>
      add({
        title: draft.title,
        description: draft.description,
        dueDate: draft.dueDate,
        // Исполнитель ДОЕЗЖАЕТ: окно создания спрашивает его всегда, а этот
        // путь когда-то выбрасывал его молча. Статуса в окне нет (ТЗ 0109),
        // приоритет вернулся (ТЗ 0111) и тоже доезжает.
        status: draft.status,
        assigneeId: draft.assigneeId,
        priority: draft.priority,
        // Связи и файлы ТОЖЕ доезжают (ревью 0109): без скоупа окно их
        // спрашивает (и прицепляет само по названию), а этот путь их терял.
        links: draft.links,
        files,
      }),
    [add],
  );

  if (!data) return null;

  return (
    <TaskCreateModal
      open
      onClose={close}
      onCreate={onCreate}
      adding={saving}
      studentId={data.studentId}
      groupId={data.groupId}
      titleAddon={titleAddon}
    />
  );
});
